import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import SchoolSettingsModal from './parts/SchoolSettingsModal';
import { useDefaultDataStore } from '../../stateManagers/store/useDefaultDataStore';
import { useSchoolDetailsStore } from '../../stateManagers/newStores/useSchoolDetailsStore'; // <-- ADDED

const TopBar: React.FC = () => {
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [initialBlink, setInitialBlink] = useState(true);
  const [connectedBlink, setConnectedBlink] = useState(false);
  const toast = useRef<Toast>(null);
  
  // 1. Get active school from default data store
  const activeSchool = useDefaultDataStore(state => state.active_school);
  
  // 2. Get school details store
  const { schoolDetails, fetchSchoolDetails } = useSchoolDetailsStore();

  // 3. Automatically fetch school details when the active school ID changes
  useEffect(() => {
    if (activeSchool?.school_id) {
          fetchSchoolDetails(activeSchool.school_id);  // No need for String() now
      console.log("hello",activeSchool.school_id)
    }
  }, [activeSchool?.school_id, fetchSchoolDetails]);

  const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const hasApiKey = activeSchool?.configured || false;

  // Initial blink effect - 3 times on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialBlink(false);
    }, 3000); // 3 blinks = 3 seconds (1s per blink)

    return () => clearTimeout(timer);
  }, []);

  // Connected status blink after 30 seconds - 3 times
  useEffect(() => {
    if (!hasApiKey) return;

    const timer = setTimeout(() => {
      setConnectedBlink(true);
      setTimeout(() => {
        setConnectedBlink(false);
      }, 3000); // 3 blinks = 3 seconds
    }, 30000); // 30 seconds delay

    return () => clearTimeout(timer);
  }, [hasApiKey]);

  return (
    <>
      <Toast ref={toast} />
      
      <style>
        {`
          @keyframes smoothBlink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
          
          .initial-blink {
            animation: smoothBlink 1s ease-in-out 3;
          }
          
          .connected-blink {
            animation: smoothBlink 1s ease-in-out 3;
          }
        `}
      </style>

      <div
        style={{
          height: '70px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 40px',
          gap: '24px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        }}
      >
        {/* Left side - School Info */}
        <div 
          className={initialBlink ? 'initial-blink' : ''}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            minWidth: '320px',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px rgba(59, 130, 246, 0.2)',
            }}
          >
            <i className="pi pi-building" style={{ fontSize: '20px', color: 'white' }}></i>
          </div>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <div
              style={{
                color: '#1f2937',
                fontSize: '15px',
                fontWeight: '600',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              {activeSchool?.school_name || 'No School Selected'}
              
              {/* Show School ID Code if fetched */}
              {schoolDetails?.school_id_code && (
                <span style={{ 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  color: '#475569', 
                  background: '#e2e8f0', 
                  padding: '2px 8px', 
                  borderRadius: '6px',
                  letterSpacing: '0.5px',
                }}>
                  {schoolDetails.school_id_code}
                </span>
              )}
            </div>
            
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              {/* Show Current Session if fetched */}
              {schoolDetails?.current_session && (
                <span style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '600' }}>
                  Session: {schoolDetails.current_session.session}
                </span>
              )}

              <span
                style={{
                  fontSize: '11px',
                  color: '#64748b',
                  fontWeight: '500',
                }}
              >
                API Status:
              </span>
              <span
                className={connectedBlink ? 'connected-blink' : ''}
                style={{
                  fontSize: '11px',
                  color: hasApiKey ? '#10b981' : '#ef4444',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <span
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: hasApiKey ? '#10b981' : '#ef4444',
                    boxShadow: hasApiKey 
                      ? '0 0 8px rgba(16, 185, 129, 0.6)' 
                      : '0 0 8px rgba(239, 68, 68, 0.6)',
                  }}
                ></span>
                {hasApiKey ? 'Connected' : 'Not Connected'}
              </span>
            </div>
          </div>
        </div>

        {/* Right side - Settings, Notifications, User */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {/* Settings Icon */}
          <button
            onClick={() => setSettingsVisible(true)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i className="pi pi-cog" style={{ fontSize: '20px', color: '#6b7280' }}></i>
          </button>

          {/* Notification Icon */}
          <button
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              padding: '8px',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i className="pi pi-bell" style={{ fontSize: '20px', color: '#6b7280' }}></i>
            <span
              style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                width: '8px',
                height: '8px',
                background: '#ef4444',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            ></span>
          </button>

          {/* User Info */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
            }}
          >
            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  color: '#1f2937',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                User
              </div>
              <div
                style={{
                  color: '#6b7280',
                  fontSize: '12px',
                  textTransform: 'capitalize',
                }}
              >
                user
              </div>
            </div>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
              }}
            >
              U
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <SchoolSettingsModal
        visible={settingsVisible}
        onHide={() => setSettingsVisible(false)}
        toast={toast}
      />
    </>
  );
};

export default TopBar;